import { Box, FormControl, Input } from "@mui/joy";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import React from "react";
import { Util } from "../Helpers/Util";
import WrappedButton from "../WrappedButton";
import SignatureDialog from "../Dialogs/SignatureDialog";
import PortalTableLayout from "../components/PortalTableLayout";

export interface ProposalInvoiceItem {
	InvoiceNumber: string;
	CustomerName: string;
	InvoiceDate: string;
	TotalAmount: number;
	Description: string;
	InvoiceHeaderID: string;
	Status: string;
	IsApproved: boolean;
	HasSignature: boolean;
}

interface ProposalInvoiceGridProps {
	buildingId: string;
	isInvoice: boolean;
}

/**
 * Shared grid component for displaying estimates (proposals) and invoices for a specific building
 */
type SortField = "number" | "date" | "amount";
type SortDirection = "asc" | "desc";

export default function ProposalInvoiceGrid(props: ProposalInvoiceGridProps) {
	const [data, setData] = React.useState<Array<ProposalInvoiceItem>>();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(25);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [loadingPdfId, setLoadingPdfId] = React.useState<string | null>(null);
	const [loadingSignedPdfId, setLoadingSignedPdfId] = React.useState<string | null>(null);
	const [loadingPaymentId, setLoadingPaymentId] = React.useState<string | null>(null);
	const [sortField, setSortField] = React.useState<SortField>("date");
	const [sortDirection, setSortDirection] = React.useState<SortDirection>("desc");
	// State for signature dialog
	const [signatureDialogOpen, setSignatureDialogOpen] = React.useState(false);
	const [selectedProposalId, setSelectedProposalId] = React.useState<string | null>(null);
	const [selectedEstimateNum, setSelectedEstimateNum] = React.useState<string | null>(null);
	const [currentFirstName, setCurrentFirstName] = React.useState("");
	const [currentLastName, setCurrentLastName] = React.useState("");
	React.useEffect(() => {
		getData();
		getUserInfo();
	}, [props.buildingId, props.isInvoice]);

	/**
	 * Fetches the current user's name for auto-populating the signature dialog
	 */
	const getUserInfo = () => {
		fetch("/User/GetPersonInfo", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((y) => {
				setCurrentFirstName(y.first_name)
				setCurrentLastName(y.last_name)
			});
	};

	/**
	 * Fetches proposals or invoices from the backend based on building ID and is_invoice flag
	 */
	const getData = () => {
		fetch("Proposal/GetProposalsInvoicesByBuilding", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				buildingsId: props.buildingId,
				isInvoice: props.isInvoice,
			}),
		})
			.then((x) => x.json())
			.then((x) => {
				setData(
					x.map((y: any) => {
						return {
							InvoiceNumber: y.invoice_number,
							CustomerName: y.building_name,
							InvoiceDate: y.invoice_date,
							TotalAmount: y.total_amount,
							Description: y.description,
							InvoiceHeaderID: y.invoice_header_id,
							Status: y.status,
							IsApproved: y.is_approved,
							HasSignature: y.has_signature,
						} as ProposalInvoiceItem;
					})
				);
			});
	};

	/**
	 * Handles column header click to toggle sorting
	 */
	const handleSort = (field: SortField) => {
		if (sortField === field) {
			// Toggle direction if clicking the same field
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			// Set new field with default descending sort
			setSortField(field);
			setSortDirection("desc");
		}
		setPageNumber(0);
	};

	/**
	 * Opens the PDF for the selected proposal or invoice
	 */
	const openPdf = (item: ProposalInvoiceItem) => {
		setLoadingPdfId(item.InvoiceHeaderID);
		fetch("Proposal/GetPdf", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				estimateNum: item.InvoiceNumber,
				isPackingList: false,
				invoiceHeaderId: item.InvoiceHeaderID,
				isInvoice: props.isInvoice,
			}),
		})
			.then((x) => x.text())
			.then((x) => {
				const blob = x;
				var tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + blob;
			})
			.finally(() => {
				setLoadingPdfId(null);
			});
	};

	/**
	 * Opens the signed estimate PDF (reconstructed from stored signature + QB PDF)
	 */
	const openSignedPdf = (item: ProposalInvoiceItem) => {
		setLoadingSignedPdfId(item.InvoiceHeaderID);
		fetch("Proposal/GetSignedPdf", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				estimateNum: item.InvoiceNumber,
				invoiceHeaderId: item.InvoiceHeaderID,
			}),
		})
			.then((x) => {
				if (!x.ok) throw new Error("Signed estimate not available");
				return x.text();
			})
			.then((fileName) => {
				const tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + fileName;
			})
			.catch(() => {
				alert("Signed estimate is not available for this proposal.");
			})
			.finally(() => {
				setLoadingSignedPdfId(null);
			});
	};

	/**
	 * Opens the QBO payment link for an invoice in a new window
	 */
	const openPaymentLink = (item: ProposalInvoiceItem) => {
		setLoadingPaymentId(item.InvoiceHeaderID);
		fetch("Proposal/GetInvoicePaymentLink", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				invoiceNumber: item.InvoiceNumber,
			}),
		})
			.then((x) => x.json())
			.then((x) => {
				if (x.paymentLink) {
					window.open(x.paymentLink, "_blank");
				} else {
					console.error("No payment link returned");
				}
			})
			.catch((err) => {
				console.error("Error getting payment link:", err);
			})
			.finally(() => {
				setLoadingPaymentId(null);
			});
	};

	/**
	 * Opens the signature dialog for approving a proposal
	 */
	const openSignatureDialog = (proposalId: string, estimateNum: string) => {
		setSelectedProposalId(proposalId);
		setSelectedEstimateNum(estimateNum);
		setSignatureDialogOpen(true);
	};

	/**
	 * Handles successful signature approval - refreshes the data
	 */
	const handleSignatureApproved = () => {
		getData();
	};

	const filteredData = data?.filter(
		(x) =>
			Util.isNullOrEmpty(globalFilter) ||
			x.CustomerName.toLowerCase().includes(globalFilter.toLowerCase()) ||
			x.InvoiceNumber.toString().includes(globalFilter) ||
			x.Description?.toLowerCase().includes(globalFilter.toLowerCase())
	);

	// Sort the filtered data
	const sortedData = filteredData?.sort((a, b) => {
		let comparison = 0;
		switch (sortField) {
			case "number":
				comparison = a.InvoiceNumber.localeCompare(b.InvoiceNumber);
				break;
			case "date":
				comparison = new Date(a.InvoiceDate).getTime() - new Date(b.InvoiceDate).getTime();
				break;
			case "amount":
				comparison = a.TotalAmount - b.TotalAmount;
				break;
		}
		return sortDirection === "asc" ? comparison : -comparison;
	});

	const rowCounts = [5, 10, 25, 50];
	const totalCount = sortedData?.length ?? 0;
	const thisRows = sortedData?.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize);

	const sortableColumns: { field: SortField; label: string }[] = [
		{ field: "number", label: props.isInvoice ? "Invoice #" : "Estimate #" },
		{ field: "date", label: "Date" },
		{ field: "amount", label: "Amount" },
	];

	return (
		<>
			<PortalTableLayout
				fillHeight={false}
				toolbar={
					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						<FormControl>
							<Input
								name="Search"
								placeholder="Global Search"
								onChange={(event) => {
									setGlobalFilter(event.target.value);
									setPageNumber(0);
								}}
								value={globalFilter}
							/>
						</FormControl>
					</Box>
				}
				pagination={{
					count: totalCount,
					page: pageNumber,
					rowsPerPage: pageSize,
					rowsPerPageOptions: rowCounts,
					onPageChange: (_event, newPage) => setPageNumber(newPage),
					onRowsPerPageChange: (event) => {
						setPageSize(parseInt(event.target.value, 10));
						setPageNumber(0);
					},
				}}>
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							{sortableColumns.map(({ field, label }) => (
								<TableCell key={field} sortDirection={sortField === field ? sortDirection : false}>
									<TableSortLabel active={sortField === field} direction={sortField === field ? sortDirection : "asc"} onClick={() => handleSort(field)}>
										{label}
									</TableSortLabel>
								</TableCell>
							))}
							<TableCell>Description</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{thisRows?.map((x) => {
							const canApprove = !props.isInvoice && !x.IsApproved && x.Status?.toLowerCase() === "pending";
							const canOpenSigned = !props.isInvoice && x.HasSignature && !canApprove;

							return (
							<TableRow key={x.InvoiceHeaderID} hover sx={{ cursor: "pointer" }}>
								<TableCell>{x.InvoiceNumber}</TableCell>
								<TableCell>{new Date(x.InvoiceDate).toLocaleDateString()}</TableCell>
								<TableCell>{Util.Formatter(x.TotalAmount, "C")}</TableCell>
								<TableCell sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.Description}</TableCell>
								<TableCell>{x.Status}</TableCell>
								<TableCell sx={{ minWidth: 250, whiteSpace: "nowrap" }}>
									<WrappedButton
										busy={loadingPdfId === x.InvoiceHeaderID}
										onClick={() => {
											openPdf(x);
										}}>
										Open PDF
									</WrappedButton>
									{/* Show Approve button for pending unapproved estimates */}
									{canApprove && (
										<WrappedButton
											sx={{ ml: 1 }}
											variant="solid"
											color="success"
											onClick={() => openSignatureDialog(x.InvoiceHeaderID, x.InvoiceNumber)}>
											Approve
										</WrappedButton>
									)}
									{canOpenSigned && (
										<WrappedButton
											sx={{ ml: 1 }}
											busy={loadingSignedPdfId === x.InvoiceHeaderID}
											onClick={() => openSignedPdf(x)}>
											Open Signed Estimate
										</WrappedButton>
									)}
									{/* Show Add Payment button for unpaid invoices */}
									{props.isInvoice && x.Status?.toLowerCase() !== "paid" && (
										<WrappedButton sx={{ ml: 1 }} color="primary" busy={loadingPaymentId === x.InvoiceHeaderID} onClick={() => openPaymentLink(x)}>
											Pay
										</WrappedButton>
									)}
								</TableCell>
							</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</PortalTableLayout>

			{/* Signature dialog for approving proposals */}
			{selectedProposalId && selectedEstimateNum && (
				<SignatureDialog
					proposalId={selectedProposalId}
					estimateNum={selectedEstimateNum}
					firstName={currentFirstName}
					lastName={currentLastName}
					open={signatureDialogOpen}
					onClose={() => setSignatureDialogOpen(false)}
					onApproved={handleSignatureApproved}
				/>
			)}
		</>
	);
}
